import axios from "axios";

import { iPaginationProps, Pagination } from "../utils/Pagination";
import { LANG } from "./i18next";
import { member, role } from "./member";
import { iUser } from "./auth";
import helpers from "@/components/helpers/helpers";
import { iCollectionAlias } from "./alias";

export const bookingEngines = [
  "none",
  "AmberGris",
  "Amenitiz",
  "AvaiBook",
  "Avvio",
  "BBPlanner",
  "Bed-And-Breakfast",
  "Beddy",
  "Bedzzle",
  "BeMyGuest",
  "Best Western Italia",
  "Blastness",
  "Bnext",
  "BookAssist",
  "BookCore",
  "Booking Designer",
  "BookingExpert",
  "Bookpage",
  "Cedyn",
  "Cloudbeds",
  "CultBooking",
  "Direct Book",
  "D-edge",
  "Dhynet",
  "EasyConsulting",
  "Ericsoft",
  "Ermes Hotels",
  "Guestcentric",
  "Hotel BB",
  "Hotel Cinque Stelle",
  "Hotel in Cloud",
  "Hotelmanagement",
  "Hotelrunner",
  "Hotetec",
  "Hotusa",
  "HyperGuest",
  "iHotelier",
  "InReception",
  "Iperbooking",
  "Kross Travel",
  "Little Hotelier",
  "Mews",
  "Mirai",
  "MyForecast",
  "MyGuestCare",
  "Neobookings",
  "Net Affinity",
  "Nozio",
  "Octorate",
  "Paraty Tech",
  "Parity Rate",
  "Passpartout",
  "Profitroom",
  "Quovai",
  "RateGain",
  "RateTiger",
  "Reservation in Cloud",
  "Roiback",
  "RoomCloud",
  "Scidoo",
  "Secure Hotel Booking",
  "Simplebooking",
  "Simplotel",
  "SiteMinder",
  "Slope",
  "Synxis",
  "Sysdat",
  "TravelClick",
  "Teamsystem",
  "Vertical Booking",
  "WebHotelier",
  "Witbooking",
  "WuBook",
  "Zak/ WoDoo",
  "other",
] as const;

export type BookingEngine = (typeof bookingEngines)[number];

export enum COLLECTION_TYPE {
  experience = "experience",
  hotel = "hotel",
}

export enum COLLECTION_PERMISSIONS {
  admin = "admin",
  update = "update",
  memebers = "memebers",
  census = "census",
  preset = "preset",
  create_nft = "create_nft",
  update_nft = "update_nft",
  delete_nft = "delete_nft",
  sale = "sale",
  set_payment_method = "set_payment_method",
  view_payment_data = "view_payment_data",
  receive_notifications = "receive_notifications",
  stealth = "stealth",
  view_events = "view_events",
}

export type experienceCensusInfo = {
  [lang in LANG]?: {
    experiences?: {
      name: string;
      description: string;
      images: string[];
    }[];
  };
};

export type hotelCensusInfo = {
  [lang in LANG]?: {
    description?: string;
    services?: { icon: string; value: string }[];
    rooms?: {
      name: string;
      services?: { icon: string; value: string; data: string }[];
      traits?: { icon: string; value: string }[];
      images?: string[];
    }[];
    poi?: {
      icon: string;
      value: string;
      text: string;
    }[];
    policies?: {
      title: string;
      text: string;
    }[];
    reviews?: {
      vote?: number;
      voteText?: string;
      date?: string;
      context?: string;
      title: string;
      text: string;
    }[];
    reviewSummary?: {
      vote?: number;
      voteText?: string;
      cleanlinessVote?: number;
      hotelConditionVote?: number;
      roomConfortVote?: number;
      serviceStaffVote?: number;
    };
  };
};

export const locationTypes = [
  "city",
  "lake",
  "mountain",
  "sea",
  "wellness",
  "countryside",
] as const;

export type hotelCensusLocationType = (typeof locationTypes)[number];

export type hotelCensus = {
  type?: "hotel";

  name?: string;
  stars?: string;
  location?: any;
  locationType?: hotelCensusLocationType;
  email?: string;
  website?: string;
  note?: string; // deprecated
  images?: Array<string>;
  info?: hotelCensusInfo;
  hotelsComSlug?: string;
  hotelsComId?: string;
  feeOnCreation?: number;
  feeMonthly?: number;
  feeFixedOnCreation?: number;
  cin?: string;
  cir?: string;
};

export type hotelStats = {
  averageBookingPrice?: number;
  averageNumberOfDaysPerStay?: number;
  averageOccupancyPercentage?: number;
  directBookingPercentage?: number;
  nonRefoundableSalesPercentage?: number;
};

// Exp - start
/*
export type experienceCategory =
  | "sport"
  | "adrenaline"
  | "city"
  | "wellness";
*/

export enum ExperienceCategory {
  sport = "Sport",
  adrenaline = "Adrenaline",
  city = "City",
  wellness = "Wellness",
}

export type experienceCensus = {
  type?: "experience";

  name?: string;
  email?: string;
  website?: string;
  location?: any;

  category?: ExperienceCategory;
  note?: string; // deprecated
};

// Exp - end

export type publicContact = {
  type: "phone" | "website" | "whatsapp" | "email";
  value: string;
};

export type priceProviders = {
  bookingEngine?: {
    type: BookingEngine;
    params: any;
  };
  hotelscom?: { id: string };
  bookingcom?: { id: string };
};

export interface iCollection {
  _id: string;
  name: string;
  groupId: string;
  gold: boolean; // TODO deprecable?
  type: string;
  paymentMethodId: string;
  members: Array<member<COLLECTION_PERMISSIONS>>;

  census?: hotelCensus;
  images: Array<string>;
  website?: string;
  phone?: string;
  email?: string;
  locationType?: hotelCensusLocationType;
  cin?: string;
  cir?: string;
  stars?: string;

  nftCount?: number;
  lastNftCreatedAt?: any;
  subscriptionStatus?: string;
  sellerPaymentStatus?: boolean;

  feeOnCreation?: number;
  feeMonthly?: number;
  feeFixedOnCreation?: number;

  trialEndDate?: Date;

  bonusCredits?: number;

  isActive: boolean;

  checkBeforeSale?: boolean;

  liveDate?: any;
  valueGenerated?: number;

  _createdAt: any;

  alias?: iCollectionAlias;
  bookingEngine: BookingEngine;
  priceProviders?: priceProviders;
  preData?: {
    bookingComId?: string;
    deltaPriceBookingCom: number;
    deltaPriceHotelsCom: number;
    advantagesOnDirectWebsite: string[];
    numberOfRooms?: number;

    bookingEngineParams?: {
      hotelId?: string;
      channelId?: string;
      otherId?: string;
    };
    redirect?: {
      exampleUrl: string;
      description: string;
      useRedirect: boolean;
    };
  };

  scriptEnabled: boolean;
  startTrackingDate?: Date;
  isOnboardingComplete?: boolean;
  tags?: hotelCensusLocationType[];
  isLoyaltyEnabled?: boolean;
  isLoyaltyEnabledAdmin?: boolean;
  loyatyStartDate?: string;
  loyatyDiscountPercentage?: number;
  takoinCredits?: number;
  appUnlocked?: boolean;
}

export const collectionRoles: role<COLLECTION_PERMISSIONS>[] = [
  {
    name: "moderator",
    permissions: [
      COLLECTION_PERMISSIONS.update,
      COLLECTION_PERMISSIONS.memebers,
      COLLECTION_PERMISSIONS.create_nft,
      COLLECTION_PERMISSIONS.delete_nft,
      COLLECTION_PERMISSIONS.update_nft,
      COLLECTION_PERMISSIONS.set_payment_method,
      COLLECTION_PERMISSIONS.view_payment_data,
      COLLECTION_PERMISSIONS.census,
      COLLECTION_PERMISSIONS.receive_notifications,
      COLLECTION_PERMISSIONS.sale,
      COLLECTION_PERMISSIONS.view_events,
    ],
    onlyAdmin: true,
  },
  {
    name: "rm",
    permissions: [
      COLLECTION_PERMISSIONS.update,
      COLLECTION_PERMISSIONS.memebers,
      COLLECTION_PERMISSIONS.set_payment_method,
      COLLECTION_PERMISSIONS.view_payment_data,
      COLLECTION_PERMISSIONS.census,
      COLLECTION_PERMISSIONS.receive_notifications,
      COLLECTION_PERMISSIONS.sale,
      COLLECTION_PERMISSIONS.view_events,
    ],
  },
  {
    name: "rmnopayments",
    permissions: [
      COLLECTION_PERMISSIONS.update,
      COLLECTION_PERMISSIONS.memebers,
      COLLECTION_PERMISSIONS.census,
      COLLECTION_PERMISSIONS.receive_notifications,
      COLLECTION_PERMISSIONS.sale,
      COLLECTION_PERMISSIONS.view_events,
    ],
  },
  {
    name: "editor",
    permissions: [
      COLLECTION_PERMISSIONS.update,
      COLLECTION_PERMISSIONS.create_nft,
      COLLECTION_PERMISSIONS.update_nft,
      COLLECTION_PERMISSIONS.census,
    ],
    onlyAdmin: true,
  },
  {
    name: "notificable",
    permissions: [COLLECTION_PERMISSIONS.receive_notifications],
  },
  {
    name: "api",
    permissions: [
      COLLECTION_PERMISSIONS.create_nft,
      COLLECTION_PERMISSIONS.update_nft,
    ],
    onlyAdmin: true,
  },
  {
    name: "member",
    permissions: [],
  },
  {
    name: "stealth_events",
    permissions: [
      COLLECTION_PERMISSIONS.stealth,
      COLLECTION_PERMISSIONS.view_events,
    ],
    onlyAdmin: true,
  },
  {
    name: "stealth_editor",
    permissions: [
      COLLECTION_PERMISSIONS.stealth,
      COLLECTION_PERMISSIONS.preset,
      COLLECTION_PERMISSIONS.census,
      COLLECTION_PERMISSIONS.update,
    ],
    onlyAdmin: true,
  },
];

export interface Destination {
  dest_id: string;
  search_type: string;
  image_url: string;
  country: string;
  label: string;
  region: string;
  type: string;
  nr_hotels: number;
  name: string;
  latitude: number;
  cc1: string;
  hotels: number;
  dest_type: string;
  city_name: string;
  lc: string;
  roundtrip: string;
  longitude: number;
  city_ufi: string | null;
}

export interface loadCollectionProps {
  page?: number;
  size?: number;
  total?: number;
  groupId?: string;
  gold?: boolean;
  query?: any;
  sort?: any;
}

export interface iPagination<T> {
  data: T[];
  total: number;
  page: number;
  size: number;
}

// WIP - new function for fetching collections
// in DataView component that use react-table
export async function fetchCollections({
  page = 1,
  size = 25,
  groupId = "",
  gold = false,
  query = {},
  sort = { name: 1 },
}): Promise<iCollection[]> {
  let url = `/collection/paginate?page=${page}&size=${size}`;

  if (groupId) {
    url += "&groupId=" + groupId;
  }

  if (typeof gold === "boolean") {
    if (gold) url += "&gold=true";
    else url += "&gold=false";
  }

  if (query) {
    url += "&query=" + JSON.stringify(query);
  }

  if (sort) {
    url += "&sort=" + JSON.stringify(sort);
  }

  const response = await axios.get(url);
  return response.data;
}

export function inviteNotAccepted(collection: iCollection, user: iUser) {
  return (
    collection.members.find((e) => e.email === user?.email)
      ?.isInviteAccepted === false
  );
}

export async function getCollections(
  params: iPaginationProps
): Promise<Pagination<iCollection>> {
  const response = await axios.get("/collection/paginate", { params });
  return response.data;
}

export async function getCollectionsInvitations(): Promise<iCollection[]> {
  const response = await axios.get("/collection/invitations");
  return response.data;
}

export async function getCollectionsAliases(
  params: iPaginationProps
): Promise<Pagination<iCollection>> {
  const response = await axios.get("/collection/alias/paginated", {
    params,
  });
  return response.data;
}
export async function getCollectionIdFromAlias(alias: string): Promise<string> {
  const response = await axios.get("/collection/alias/getId", {
    params: { alias },
  });
  return response.data;
}

export async function createCollection(payload: any): Promise<iCollection> {
  const response = await axios.post("/collection", payload);
  return response.data;
}

export async function patchCollection(payload: any): Promise<iCollection> {
  const response = await axios.patch("/collection", payload);
  return response.data;
}

export async function getCollection(id: string): Promise<iCollection> {
  const response = await axios.get("/collection/" + id);
  return response.data;
}

// seems to be duplicated
export async function updateCollection(payload: any): Promise<iCollection> {
  const response = await axios.patch("/collection/", payload);
  return response.data;
}

export async function BookingComSearchRegions(params: {
  query: string;
}): Promise<Destination[]> {
  const res = await axios.post("/metasearch/regions", params);
  return res.data;
}

export async function inviteMemberToCollection(
  payload: any
): Promise<iCollection> {
  const response = await axios.post("/collection/member", payload);
  return response.data;
}

export async function acceptInviteCollection(payload: any) {
  const response = await axios.post("/collection/join", payload);
  return response.data;
}

export async function declineInviteCollection(payload: any) {
  const response = await axios.post("/collection/decline", payload);
  return response.data;
}

export async function removeMemberFromCollection(payload: any): Promise<any> {
  const response = await axios.delete("/collection/member", { data: payload });
  return response.data;
}

export async function setCollectionPermissionsPerUser(payload: {
  collectionId: string;
  email: string;
  permissions: COLLECTION_PERMISSIONS[];
}): Promise<any> {
  const response = await axios.patch("/collection/member", payload);
  return response.data;
}

export async function leaveCollection(payload: any) {
  const response = await axios.post("/collection/leave", payload);
  return response.data;
}

export async function deleteCollection(payload: any) {
  const response = await axios.delete("/collection", { data: payload });
  return response.data;
}

export async function transferCollection(payload: any) {
  const response = await axios.patch("/collection/transfer-ownership", payload);
  return response.data;
}

export async function setTrialDate(payload: any) {
  const response = await axios.post("/collection/trial", payload);
  return response.data;
}

export async function getCollectionCensusInfoHotelsCom(payload: any) {
  const response = await axios.post("/hotelscom/all-info", payload);
  return response.data;
}

export async function getCollectionCensusInfoRoomsHoltelsCom(payload: any) {
  const response = await axios.post("/hotelscom/rooms", payload);
  return response.data;
}

export async function getCollectionCensusInfoReviewsHoltelsCom(payload: any) {
  const response = await axios.post("/hotelscom/reviews", payload);
  return response.data;
}

export async function getPaginated(
  params: iPaginationProps
): Promise<iPagination<iCollection>> {
  const response = await axios.get("/collection/paginate-public", {
    params,
  });
  return response.data;
}

export interface iCreateFakeHotelSupply {
  collectionId: string;
  guestsAmount: number;
  guestsKidsAmount: number;
  nightsAmount: number;
  startFromDate: string;
  roomType: string;
  owner: string;
  putForSale: boolean;
  discountFromMarket: number;
  price: number;
}

export async function createFakeHotelSupply(payload: iCreateFakeHotelSupply) {
  //console.log(payload);
  const respones = await axios.post(
    "/collection/create-hotel-artificial-supply",
    payload
  );
  return respones.data;
}

export async function sendOnboardingEmail(id: string): Promise<iCollection> {
  const response = await axios.post("/collection/send-onboarding-email", {
    collectionId: id,
  });
  return response.data;
}

export type GuessedBookingEngine = {
  status:
    | "notFoundError"
    | "success"
    | "guessed"
    | "urlCallError"
    | "validationError";
  url?: string;
  guesses?: BookingEngine[];
  priceProvider?: priceProviders["bookingEngine"];
};

export async function detectBookingEngine(
  url: string
): Promise<GuessedBookingEngine> {
  const response = await axios.get("/collection/detect-booking-engine", {
    params: {
      url,
    },
  });
  return response.data;
}

export function onBoardingCompleted(collection: iCollection) {
  try {
    if (collection.isOnboardingComplete) return true;

    if (!collection.census || !collection.preData) {
      return false;
    }

    const hasDetails = helpers.hasRequiredProps(collection, [
      "name",
      "website",
      "census.location",
      "preData.deltaPriceBookingCom",
      "preData.deltaPriceHotelsCom",
    ]);

    const hasbookingComId = helpers.hasRequiredProps(collection, [
      "preData.bookingComId",
    ]);

    if (hasbookingComId === false) {
      const createdAt = new Date(collection._createdAt);
      const prev2025 = new Date("2025-02-01T00:00:00Z");
      return createdAt < prev2025;
    }

    return hasDetails;
  } catch (error) {
    console.log(error);
  }
}

export function getCollectionSettings(collection: iCollection) {
  return {
    trialEndDate: collection.trialEndDate,
    feeOnCreation: collection.feeOnCreation,
    feeMonthly: collection.feeMonthly,
    feeFixedOnCreation: collection.feeFixedOnCreation,
    bonusCredits: collection.bonusCredits,
  };
}

export function onBoardingLastStep(collection: iCollection) {
  let step = "selecthotel";

  if (!collection?.preData?.advantagesOnDirectWebsite) step = "hotelbenefits";
  if (
    !collection?.preData?.deltaPriceBookingCom ||
    !collection?.preData?.deltaPriceHotelsCom
  )
    step = "hotelprice";

  if (collection?.preData?.bookingComId) step = "hoteldata";
  if (!collection?.census) step = "hoteldata";
  if (!collection?.census?.location) step = "hoteldata";
  if (!collection?.name || !collection?.preData?.bookingComId)
    step = "selecthotel";

  return step;
}
